import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Calendar, Plus, X } from "lucide-react";
import { getLogements, getDisponibilites, createDisponibilite, deleteDisponibilite } from "@/lib/api";

export default function CalendrierPage() {
  const [logements, setLogements] = useState([]);
  const [selectedLogement, setSelectedLogement] = useState(null);
  const [disponibilites, setDisponibilites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    date_debut: "",
    date_fin: "",
    motif: "maintenance"
  });

  useEffect(() => {
    loadLogements();
  }, []);

  useEffect(() => {
    if (selectedLogement) {
      loadDisponibilites(selectedLogement);
    }
  }, [selectedLogement]);

  const loadLogements = async () => {
    try {
      const res = await getLogements();
      setLogements(res.data);
      if (res.data.length > 0) {
        setSelectedLogement(res.data[0].id);
      }
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const loadDisponibilites = async (logementId) => {
    try {
      const res = await getDisponibilites(logementId);
      setDisponibilites(res.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createDisponibilite({
        logement_id: selectedLogement,
        ...formData
      });
      toast.success("Période bloquée !");
      setShowModal(false);
      setFormData({ date_debut: "", date_fin: "", motif: "maintenance" });
      loadDisponibilites(selectedLogement);
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce blocage ?")) return;
    try {
      await deleteDisponibilite(id);
      toast.success("Blocage supprimé !");
      loadDisponibilites(selectedLogement);
    } catch (error) {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div data-testid="calendrier-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-display text-[#004333]">Calendrier & Disponibilités</h2>
          <p className="text-gray-500">Gérez les périodes de blocage manuelles</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center gap-2"
          disabled={!selectedLogement}
          data-testid="add-blocage-btn"
        >
          <Plus className="w-4 h-4" />
          Bloquer une période
        </button>
      </div>

      {logements.length > 0 ? (
        <div className="grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <h3 className="font-semibold text-[#004333] mb-4">Logements</h3>
            <div className="space-y-2">
              {logements.map((logement) => (
                <button
                  key={logement.id}
                  onClick={() => setSelectedLogement(logement.id)}
                  className={`w-full text-left p-3 transition-colors ${
                    selectedLogement === logement.id
                      ? "bg-[#004333] text-white"
                      : "bg-white border border-gray-200 hover:border-[#004333]"
                  }`}
                >
                  <div className="font-medium">{logement.titre}</div>
                  <div className="text-sm opacity-70">{logement.ville}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-200 p-6">
              <h3 className="font-display text-xl text-[#004333] mb-6 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C9922A]" />
                Périodes bloquées
              </h3>

              {disponibilites.length > 0 ? (
                <div className="space-y-3">
                  {disponibilites.map((dispo) => (
                    <div
                      key={dispo.id}
                      className="flex items-center justify-between p-4 bg-gray-50"
                    >
                      <div>
                        <div className="font-medium text-[#004333]">
                          {dispo.date_debut} → {dispo.date_fin}
                        </div>
                        <div className="text-sm text-gray-500 capitalize">
                          {dispo.motif === "maintenance" && "Maintenance / Travaux"}
                          {dispo.motif === "usage_personnel" && "Usage personnel"}
                          {dispo.motif === "reservation" && "Réservation confirmée"}
                        </div>
                      </div>
                      {dispo.motif !== "reservation" && (
                        <button
                          onClick={() => handleDelete(dispo.id)}
                          className="p-2 text-gray-400 hover:text-red-500"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucune période bloquée pour ce logement.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 p-16 text-center">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-display text-xl text-[#004333] mb-2">Aucun logement</h3>
          <p className="text-gray-500">Ajoutez d'abord des logements pour gérer le calendrier.</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white max-w-md w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-display text-xl text-[#004333]">Bloquer une période</h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date début</label>
                  <input
                    type="date"
                    required
                    value={formData.date_debut}
                    onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                    className="input-hh"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
                  <input
                    type="date"
                    required
                    value={formData.date_fin}
                    onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                    className="input-hh"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Motif</label>
                <select
                  value={formData.motif}
                  onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                  className="input-hh"
                >
                  <option value="maintenance">Maintenance / Travaux</option>
                  <option value="usage_personnel">Usage personnel</option>
                </select>
              </div>
              
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Annuler
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Bloquer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
